import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import LoadingCover from '../common/LoadingCover';
import { useNavigate } from 'react-router-dom';
import {
  useGetPendingReviewBSCsQuery,
  useApproveBSCMutation,
  useRejectBSCMutation,
} from '../../redux/balancedScorecardApi';
import { BalancedScorecard, PERFORMANCE_RATINGS } from './bscTypes';

// Helper to get grade color
const getGradeColor = (grade: string): string => {
  const rating = PERFORMANCE_RATINGS.find(r => r.grade === grade);
  return rating?.colorCode || '#9e9e9e';
};

// Helper to format date
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const BSCPendingReview: React.FC = () => {
  const navigate = useNavigate();
  const { data: pendingBSCs, isLoading, error, refetch } = useGetPendingReviewBSCsQuery();
  const [approveBSC, { isLoading: isApproving }] = useApproveBSCMutation();
  const [rejectBSC, { isLoading: isRejecting }] = useRejectBSCMutation();

  const [selectedBSC, setSelectedBSC] = useState<BalancedScorecard | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [managerComments, setManagerComments] = useState('');

  if (isLoading) return <LoadingCover />;

  if (error) {
    return (
      <PageWithHeader>
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">Error loading pending reviews</Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/reviews/bsc')}
              sx={{ mt: 2 }}
            >
              Back to BSC
            </Button>
          </Paper>
        </Box>
      </PageWithHeader>
    );
  }

  const handleViewDetails = (bsc: BalancedScorecard) => {
    // Navigate to the full BSC review page
    navigate(`/reviews/bsc/review/${bsc.id}`);
  };

  const handleApproveClick = (bsc: BalancedScorecard) => {
    setSelectedBSC(bsc);
    setManagerComments('');
    setShowApproveDialog(true);
  };

  const handleRejectClick = (bsc: BalancedScorecard) => {
    setSelectedBSC(bsc);
    setManagerComments('');
    setShowRejectDialog(true);
  };

  const handleApprove = async () => {
    if (!selectedBSC?.id) return;
    try {
      await approveBSC({ id: selectedBSC.id, managerComments }).unwrap();
      setShowApproveDialog(false);
      setSelectedBSC(null);
      refetch();
    } catch (err) {
      console.error('Error approving BSC:', err);
      alert('Error approving BSC');
    }
  };

  const handleReject = async () => {
    if (!selectedBSC?.id || !managerComments.trim()) {
      alert('Please provide rejection comments');
      return;
    }
    try {
      await rejectBSC({ id: selectedBSC.id, managerComments }).unwrap();
      setShowRejectDialog(false);
      setSelectedBSC(null);
      refetch();
    } catch (err) {
      console.error('Error rejecting BSC:', err);
      alert('Error rejecting BSC');
    }
  };

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            borderRadius: 4,
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/reviews/bsc')}
                variant="outlined"
              >
                Back
              </Button>
              <Box>
                <Typography variant="h4" fontWeight="bold" color="primary.dark">
                  Pending BSC Reviews
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and approve BSC submissions from your direct reports
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={<AssessmentIcon />}
              label={`${pendingBSCs?.length || 0} Pending`}
              color="warning"
              variant="outlined"
            />
          </Box>
        </Paper>

        {/* No pending reviews */}
        {(!pendingBSCs || pendingBSCs.length === 0) && (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              All Caught Up!
            </Typography>
            <Typography color="text.secondary">
              No BSC submissions pending your review.
            </Typography>
          </Paper>
        )}

        {/* Pending Reviews List */}
        {pendingBSCs && pendingBSCs.length > 0 && (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Job Title</strong></TableCell>
                  <TableCell><strong>Period</strong></TableCell>
                  <TableCell><strong>Score</strong></TableCell>
                  <TableCell><strong>Grade</strong></TableCell>
                  <TableCell><strong>Submitted</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingBSCs.map((bsc) => (
                  <TableRow key={bsc.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {bsc.employeeName || 'Unknown Employee'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {bsc.jobDescriptionTitle || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{bsc.reviewPeriod}</TableCell>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {bsc.totalWeightedScore?.toFixed(1) || '-'}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bsc.finalGrade || '-'}
                        size="small"
                        sx={{
                          bgcolor: getGradeColor(bsc.finalGrade || ''),
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(bsc.submittedAt)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDetails(bsc)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleApproveClick(bsc)}
                          disabled={isApproving}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => handleRejectClick(bsc)}
                          disabled={isRejecting}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Detail View Dialog */}
        <Dialog
          open={showDetailDialog}
          onClose={() => setShowDetailDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                BSC Details - {selectedBSC?.employeeName}
              </Typography>
              <Chip
                label={`Grade: ${selectedBSC?.finalGrade || '-'}`}
                sx={{
                  bgcolor: getGradeColor(selectedBSC?.finalGrade || ''),
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedBSC && (
              <Stack spacing={3}>
                {/* Summary */}
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">Period</Typography>
                      <Typography fontWeight="bold">{selectedBSC.reviewPeriod}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">Total Score</Typography>
                      <Typography fontWeight="bold">{selectedBSC.totalWeightedScore?.toFixed(1)}%</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">Bonus Entitlement</Typography>
                      <Typography fontWeight="bold">{selectedBSC.bonusEntitlement || 0}%</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">Job Title</Typography>
                      <Typography fontWeight="bold">{selectedBSC.jobDescriptionTitle || '-'}</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Perspectives Breakdown */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Perspectives Breakdown
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell><strong>Perspective</strong></TableCell>
                          <TableCell align="right"><strong>Weight</strong></TableCell>
                          <TableCell align="right"><strong>Score</strong></TableCell>
                          <TableCell align="right"><strong>Weighted Score</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedBSC.perspectives?.map((perspective, index) => (
                          <TableRow key={index}>
                            <TableCell>{perspective.name}</TableCell>
                            <TableCell align="right">{perspective.weight}%</TableCell>
                            <TableCell align="right">{perspective.perspectiveTotal?.toFixed(1) || '-'}%</TableCell>
                            <TableCell align="right">
                              {((perspective.perspectiveTotal || 0) * perspective.weight / 100).toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Employee Comments */}
                {selectedBSC.employeeComments && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Employee Comments
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography>{selectedBSC.employeeComments}</Typography>
                    </Paper>
                  </Box>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailDialog(false)}>Close</Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => {
                setShowDetailDialog(false);
                handleApproveClick(selectedBSC!);
              }}
            >
              Approve
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => {
                setShowDetailDialog(false);
                handleRejectClick(selectedBSC!);
              }}
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approve Dialog */}
        <Dialog open={showApproveDialog} onClose={() => setShowApproveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Approve BSC</DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              You are about to approve the BSC submission for <strong>{selectedBSC?.employeeName}</strong> for period <strong>{selectedBSC?.reviewPeriod}</strong>.
            </Alert>
            <TextField
              label="Manager Comments (Optional)"
              multiline
              rows={3}
              fullWidth
              value={managerComments}
              onChange={(e) => setManagerComments(e.target.value)}
              placeholder="Add any feedback or comments..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApprove}
              disabled={isApproving}
            >
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reject BSC</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are about to reject the BSC submission for <strong>{selectedBSC?.employeeName}</strong>. Please provide a reason.
            </Alert>
            <TextField
              label="Rejection Reason (Required)"
              multiline
              rows={3}
              fullWidth
              required
              value={managerComments}
              onChange={(e) => setManagerComments(e.target.value)}
              placeholder="Please explain why this BSC is being rejected..."
              error={!managerComments.trim()}
              helperText={!managerComments.trim() ? 'Rejection reason is required' : ''}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={isRejecting || !managerComments.trim()}
            >
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageWithHeader>
  );
};

export default BSCPendingReview;
